import React, { useCallback, useEffect, FC, useRef } from "react";
import { Box, Stack } from "@mui/system";
import { Divider, IconButton, Typography, useTheme } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";
import { Link } from "gatsby";
import { globalHistory } from "@reach/router";
import { NAV_BAR_MENU_PAGES } from "../constants";
import Image from "./Image";

export type NavBarProps = {
  inverted?: boolean;
  menuOpen: boolean;
  toggleMenu: () => void;
};

const NavBar: FC<NavBarProps> = ({ menuOpen, inverted, toggleMenu }) => {
  const theme = useTheme();
  const barRef = useRef<HTMLDivElement>(null);
  const scrollRef = useRef(0);
  const lastScrollCount = useRef<{ direction: "up" | "down"; count: number }>({
    direction: "down",
    count: 0,
  });

  const onScroll = useCallback(() => {
    if (!barRef.current) return;

    const thresholdForFadingUp = 13;
    const thresholdForFadingDown = 18;

    const isScrollingUp = scrollRef.current > window.scrollY;
    const isScrollingDown = scrollRef.current < window.scrollY;
    scrollRef.current = window.scrollY;

    if (
      isScrollingDown &&
      lastScrollCount.current.direction === "down" &&
      lastScrollCount.current.count > thresholdForFadingDown
    ) {
      const barRefElementHeight = barRef.current.offsetHeight;
      barRef.current.style.top = `-${barRefElementHeight}px`;
    }

    if (
      (isScrollingUp &&
        lastScrollCount.current.direction === "up" &&
        lastScrollCount.current.count > thresholdForFadingUp) ||
      window.scrollY < 100
    ) {
      barRef.current.style.top = "0";
    }

    if (isScrollingUp && lastScrollCount.current.direction === "down") {
      lastScrollCount.current.count = 1;
      lastScrollCount.current.direction = "up";
      return;
    }

    if (isScrollingDown && lastScrollCount.current.direction === "up") {
      lastScrollCount.current.count = 1;
      lastScrollCount.current.direction = "down";
      return;
    }

    lastScrollCount.current.count += 1;
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <Box component="nav">
      <Box
        height={{ xs: "72px", sm: "80px", md: "83px" }}
        ref={barRef}
        zIndex={100}
        sx={{
          position: "fixed",
          transition: "top ease 500ms",
          left: 0,
          right: 0,
          top: 0,
          py: { xs: 2, sm: 3 },
          color: "text.primary",
          backgroundColor: "background.default",
          opacity: 0.9,
        }}
      >
        <Box
          sx={{
            height: "100%",
            maxWidth: theme.customSpacing?.containerMaxWidth,
            px: theme.customSpacing?.marginSide,
            m: "auto",
            position: "relative",
          }}
        >
          <Stack
            height="100%"
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Stack
              justifyContent="center"
              sx={{
                height: { xs: "12px", sm: "13px", md: "15px" },
                width: { xs: "76px", sm: "82px", md: "95px" },
              }}
            >
              <Link
                to="/"
                style={{
                  height: "100%",
                  width: "100%",
                }}
              >
                <Image alt="Mettle logo" name="mettle-logo" />
              </Link>
            </Stack>

            <Stack direction="row" sx={{ display: { xs: "none", sm: "flex" } }}>
              {NAV_BAR_MENU_PAGES.map((pageData, index, arr) => {
                const isLastPositionOfArray = index === arr.length - 1;
                return (
                  <Box
                    key={pageData.path}
                    mr={!isLastPositionOfArray ? { xs: 3.5 } : {}}
                    sx={{
                      height: "auto",
                      color: globalHistory.location.pathname.includes(
                        pageData.path.replace(/\//gi, "")
                      )
                        ? "text.primary"
                        : "text.secondary",
                    }}
                  >
                    <Link
                      color="inherit"
                      to={pageData.path}
                      onClick={pageData.onClick}
                    >
                      <Typography variant="body2" my={0}>
                        {pageData.name}
                      </Typography>
                    </Link>
                  </Box>
                );
              })}
            </Stack>
            <IconButton
              aria-label="menu"
              edge="end"
              color="inherit"
              onClick={(e) => {
                e.stopPropagation();
                toggleMenu();
              }}
              sx={{
                paddingTop: 0,
                paddingBottom: 0,
                paddingLeft: 0,
                display: {
                  xs: "block",
                  sm: "none",
                },
              }}
            >
              {menuOpen ? <CloseIcon /> : <MenuIcon />}
            </IconButton>
          </Stack>
        </Box>
        <Divider
          sx={{
            position: "absolute",
            width: "100%",
            bottom: 0,
          }}
        />
      </Box>
    </Box>
  );
};

export default NavBar;
